import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

interface CourseRatingProps {
  courseId: string;
  currentRating?: number;
  currentReview?: string;
  onRatingSubmitted: (rating: number, review: string) => void;
}

const CourseRating: React.FC<CourseRatingProps> = ({
  courseId,
  currentRating = 0,
  currentReview = '',
  onRatingSubmitted
}) => {
  const [rating, setRating] = useState(currentRating);
  const [review, setReview] = useState(currentReview);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to submit a rating');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses/${courseId}/review`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            rating,
            review: review.trim()
          })
        }
      );

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Thank you for your rating!');
        onRatingSubmitted(rating, review);
      } else {
        toast.error(result.message || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingText = (stars: number) => {
    switch (stars) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return 'Select a rating';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate This Course</h3>
      
      <div className="mb-6">
        <div className="flex items-center space-x-1 mb-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              onClick={() => setRating(star)}
              className="focus:outline-none"
            >
              {star <= (hoveredRating || rating) ? (
                <StarIconSolid className="h-8 w-8 text-yellow-400" />
              ) : (
                <StarIcon className="h-8 w-8 text-gray-300 hover:text-yellow-400 transition-colors" />
              )}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-600">
          {getRatingText(hoveredRating || rating)}
        </p>
      </div>

      <div className="mb-6">
        <label htmlFor="review" className="block text-sm font-medium text-gray-700 mb-2">
          Write a Review (Optional)
        </label>
        <textarea
          id="review"
          rows={4}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder="Share your thoughts about this course..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">
          {review.length}/500 characters
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={() => {
            setRating(currentRating);
            setReview(currentReview);
          }}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleSubmitRating}
          disabled={rating === 0 || submitting}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            rating > 0 && !submitting
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Submitting...' : 'Submit Rating'}
        </button>
      </div>

      {currentRating > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            You have already rated this course {currentRating} star{currentRating !== 1 ? 's' : ''}.
            {currentReview && (
              <span className="block mt-1 italic">"{currentReview}"</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default CourseRating;